import { supabase } from '../lib/supabaseClient';

export const updateStoryTitleDB = async (storyId, title) => {
  const { data, error } = await supabase
    .from('stories')
    .update({ title, updated_at: new Date().toISOString() })
    .eq('id', storyId)
    .select()
    .single();

  if (error) {
    console.error('Error updating story title:', error);
    throw error;
  }
  return data;
};

export const updateStoryMetadata = async (storyId, state) => {
  const { world, character, ai } = state;
  const { data, error } = await supabase
    .from('stories')
    .update({
      world_genre: world?.genre,
      world_setting: world?.setting,
      world_pov: world?.pov,
      world_tone: world?.tone,
      character_name: character?.name,
      character_nickname: character?.nickname,
      character_preferred_reference: character?.preferredReference,
      character_role: character?.role,
      character_backstory: character?.backstory,
      character_avatar: character?.avatar,
      ai_model: ai?.model || 'minimax/minimax-m2-her',
      ai_image_enabled: ai?.imageGenerator?.enabled || false,
      ai_image_style: ai?.imageGenerator?.style || 'realistic',
      is_public: world?.isPublic || false,
      background_image: world?.backgroundImage,
      updated_at: new Date().toISOString(),
    })
    .eq('id', storyId)
    .select()
    .single();

  if (error) {
    console.error('Error updating story metadata:', error);
    throw error;
  }
  return data;
};

export const getQuests = async (storyId) => {
  const { data, error } = await supabase.from('quests').select('*').eq('story_id', storyId);
  if (error) { console.error('Error fetching quests:', error); throw error; }
  return data;
};

export const saveQuest = async (storyId, quest, userId = null) => {
  const { data, error } = await supabase.from('quests').insert([{ story_id: storyId, title: quest.title, description: quest.description, status: quest.status, reward: quest.reward, difficulty: quest.difficulty, user_id: userId }]).select().single();
  if (error) { console.error('Error saving quest:', error); throw error; }
  return data;
};

export const updateQuestDB = async (questId, updates) => {
  const { data, error } = await supabase.from('quests').update(updates).eq('id', questId).select().single();
  if (error) { console.error('Error updating quest:', error); throw error; }
  return data;
};

export const getChapters = async (storyId) => {
  const { data, error } = await supabase.from('chapters').select('*').eq('story_id', storyId).order('created_at', { ascending: false });
  if (error) { console.error('Error fetching chapters:', error); throw error; }
  return data;
};


export const updateChapterContentDB = async (chapterId, newContent) => {
  const { data, error } = await supabase.from('chapters').update({ content: newContent }).eq('id', chapterId).select().single();
  if (error) { console.error('Error updating chapter:', error); throw error; }
  return data;
};

export const toggleChapterBookmarkDB = async (chapterId, bookmarked) => {
  const { data, error } = await supabase.from('chapters').update({ bookmarked }).eq('id', chapterId).select().single();
  if (error) { console.error('Error toggling chapter bookmark:', error); throw error; }
  return data;
};

export const saveChapter = async (storyId, chapter) => {
  const { data, error } = await supabase.from('chapters').insert([{ story_id: storyId, title: chapter.title, summary: chapter.summary, content: chapter.content, word_count: chapter.wordCount }]).select().single();
  if (error) { console.error('Error saving chapter:', error); throw error; }
  await supabase.from('stories').update({ updated_at: new Date().toISOString() }).eq('id', storyId);
  return data;
};

export const deleteChapter = async (chapterId) => {
  // get story id first
  const { data: fetch } = await supabase.from('chapters').select('story_id').eq('id', chapterId).single();
  const storyId = fetch?.story_id;

  const { data, error } = await supabase.from('chapters').delete().eq('id', chapterId);
  if (error) { console.error('Error deleting chapter:', error); throw error; }

  if (storyId) {
    await supabase.from('stories').update({ updated_at: new Date().toISOString() }).eq('id', storyId);
  }
  return data;
};

export const getGalleryImages = async (storyId) => {
  const { data, error } = await supabase.from('gallery_images').select('*').eq('story_id', storyId).order('created_at', { ascending: false });
  if (error) { console.error('Error fetching gallery images:', error); throw error; }
  return data;
};

export const saveGalleryImage = async (storyId, image) => {
  const { data, error } = await supabase.from('gallery_images').insert([{ story_id: storyId, url: image.url, title: image.title, base_scene: image.baseScene, modifications: image.modifications || 0, history: image.history || [], user_id: image.user_id || null }]).select().single();
  if (error) { console.error('Error saving gallery image:', error); throw error; }
  return data;
};

export const updateGalleryImageDB = async (imageId, updateData) => {
  const payload = {};
  if (updateData.url !== undefined) payload.url = updateData.url;
  if (updateData.title !== undefined) payload.title = updateData.title;
  if (updateData.baseScene !== undefined) payload.base_scene = updateData.baseScene;
  if (updateData.modifications !== undefined) payload.modifications = updateData.modifications;
  if (updateData.history !== undefined) payload.history = updateData.history;

  const { data, error } = await supabase.from('gallery_images').update(payload).eq('id', imageId).select().single();
  if (error) { console.error('Error updating gallery image:', error); throw error; }
  return data;
};

export const deleteGalleryImageDB = async (imageId) => {
  const { error } = await supabase.from('gallery_images').delete().eq('id', imageId);
  if (error) { console.error('Error deleting gallery image:', error); throw error; }
};

export const getStoryIdeas = async (userId) => {
  const { data, error } = await supabase.from('story_ideas').select('*').eq('user_id', userId).order('created_at', { ascending: false });
  if (error) { console.error('Error fetching story ideas:', error); throw error; }
  return data;
};

export const saveStoryIdea = async (userId, idea) => {
  const { data, error } = await supabase.from('story_ideas').insert([{ user_id: userId, title: idea.title, hook: idea.hook, theme: idea.theme }]).select().single();
  if (error) { console.error('Error saving story idea:', error); throw error; }
  return data;
};

export const getCollaborators = async (storyId) => {
  const { data, error } = await supabase.from('collaborators').select('*, profiles(username, subscription_tier), characters(id, name, nickname, preferred_reference, avatar)').eq('story_id', storyId);
  if (error) { console.error('Error fetching collaborators:', error); throw error; }
  return data;
};

export const createStory = async (userId, title, state) => {
  const { world, character, ai } = state;
  const { data, error } = await supabase
    .from('stories')
    .insert([
      {
        owner_id: userId,
        title: title || 'Untitled Story',
        world_genre: world?.genre,
        world_setting: world?.setting,
        world_pov: world?.pov,
        world_tone: world?.tone,
        character_name: character?.name,
        character_nickname: character?.nickname,
        character_preferred_reference: character?.preferredReference,
        character_role: character?.role,
        character_backstory: character?.backstory,
        character_avatar: character?.avatar,
        ai_model: ai?.model || 'minimax/minimax-m2-her',
        ai_image_enabled: ai?.imageGenerator?.enabled || false,
        ai_image_style: ai?.imageGenerator?.style || 'realistic',
        background_image: world?.backgroundImage,
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error creating story:', error);
    throw error;
  }
  return data;
};

export const toggleStoryPublicStatus = async (storyId, isPublic) => {
  const { data, error } = await supabase
    .from('stories')
    .update({ is_public: isPublic, updated_at: new Date().toISOString() })
    .eq('id', storyId)
    .select()
    .single();

  if (error) {
    console.error('Error toggling story public status:', error);
    throw error;
  }
  return data;
};

export const requestToJoinStory = async (storyId, userId, characterId) => {
  // If characterId is provided we use it, but for a simple request, maybe just user_id is enough initially,
  // let's insert a pending collaborator record.
  const payload = {
    story_id: storyId,
    user_id: userId,
    role: 'Co-Author',
    status: 'Pending'
  };
  if (characterId) payload.character_id = characterId;

  const { data, error } = await supabase
    .from('collaborators')
    .insert([payload])
    .select()
    .single();

  if (error) {
    console.error('Error requesting to join story:', error);
    throw error;
  }
  return data;
};

export const updateUsername = async (userId, username) => {
  const { data, error } = await supabase
    .from('profiles')
    .update({ username })
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error updating username:', error);
    throw error;
  }
  return data;
};

const touchStory = async (storyId) => {
  if (!storyId) return;
  await supabase.from('stories').update({ updated_at: new Date().toISOString() }).eq('id', storyId);
};

export const updateStoryEntry = async (entryId, content, storyId = null) => {
  const { data, error } = await supabase
    .from('story_entries')
    .update({ content })
    .eq('id', entryId)
    .select()
    .single();

  if (error) {
    console.error('Error updating story entry:', error);
    throw error;
  }
  if (storyId) await touchStory(storyId);
  else if (data?.story_id) await touchStory(data.story_id);

  return data;
};

export const toggleStoryEntryBookmark = async (entryId, bookmarked, storyId = null) => {
  const { data, error } = await supabase
    .from('story_entries')
    .update({ bookmarked })
    .eq('id', entryId)
    .select()
    .single();

  if (error) {
    console.error('Error toggling story entry bookmark:', error);
    throw error;
  }
  if (storyId) await touchStory(storyId);
  else if (data?.story_id) await touchStory(data.story_id);

  return data;
};

export const deleteAllStoryEntriesDB = async (storyId) => {
  const { data, error } = await supabase
    .from('story_entries')
    .delete()
    .eq('story_id', storyId);

  if (error) {
    console.error('Error deleting all story entries for story:', error);
    throw error;
  }
  return data;
};

export const deleteStoryEntryDB = async (entryId, storyId = null) => {
  let sId = storyId;
  if (!sId) {
    // try to get it first
    const { data: fetch } = await supabase.from('story_entries').select('story_id').eq('id', entryId).single();
    if (fetch?.story_id) sId = fetch.story_id;
  }

  const { data, error } = await supabase
    .from('story_entries')
    .delete()
    .eq('id', entryId);

  if (error) {
    console.error('Error deleting story entry:', error);
    throw error;
  }

  if (sId) await touchStory(sId);
  return data;
};

export const saveStoryEntry = async (storyId, entry, orderIndex) => {
  const { data, error } = await supabase
    .from('story_entries')
    .insert([
      {
        story_id: storyId,
        role: entry.role,
        content: entry.content,
        order_index: orderIndex,
        user_id: entry.user_id || null,
        character_name: entry.character_name || null,
        character_nickname: entry.character_nickname || null,
        character_preferred_reference: entry.character_preferred_reference || null
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error saving story entry:', error);
    throw error;
  }
  await touchStory(storyId);
  return data;
};

export const sendPokeDB = async (targetUserId, senderId, senderName, storyTitle, storyId) => {
  const { error } = await supabase
    .from('pokes')
    .insert([
      {
        receiver_id: targetUserId,
        sender_id: senderId,
        sender_name: senderName,
        story_title: storyTitle,
        story_id: storyId || null
      }
    ]);

  if (error) {
    console.error('Error sending poke to db:', error);
    throw error;
  }
  return true;
};

export const getUserProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
  return data;
};

export const updateSubscriptionTier = async (userId, tier) => {
  const { data, error } = await supabase
    .from('profiles')
    .update({ subscription_tier: tier })
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error updating subscription tier:', error);
    throw error;
  }
  return data;
};

export const getPublicStories = async () => {
  const { data, error } = await supabase
    .from('stories')
    .select('*, chapters(id), profiles:owner_id(username)')
    .eq('is_public', true)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Error fetching public stories:', error);
    throw error;
  }
  return data;
};

export const getUserStories = async (userId) => {
  const { data, error } = await supabase
    .from('stories')
    .select('*, chapters(id), collaborators(role, status, profiles(username))')
    .eq('owner_id', userId)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Error fetching user stories:', error);
    throw error;
  }
  return data;
};

export const getSharedStories = async (userId) => {
  const { data, error } = await supabase
    .from('collaborators')
    .select(`
      story_id,
      role,
      status,
      stories (*, profiles(username))
    `)
    .eq('user_id', userId)
    .neq('status', 'Declined');

  if (error) {
    console.error('Error fetching shared stories:', error);
    throw error;
  }

  // Extract and return just the story objects with the role attached
  return data.filter(c => c.stories !== null).map(collab => ({
    ...collab.stories,
    userRole: collab.role,
    userStatus: collab.status
  }));
};

export const getMostRecentStoryForUser = async (userId) => {
  const { data, error } = await supabase
    .from('stories')
    .select('*')
    .eq('owner_id', userId)
    .order('updated_at', { ascending: false })
    .limit(1)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No rows found, which is fine
      return null;
    }
    console.error('Error fetching most recent story:', error);
    throw error;
  }
  return data;
};

export const getStoryById = async (storyId) => {
  const { data, error } = await supabase
    .from('stories')
    .select('*')
    .eq('id', storyId)
    .single();

  if (error) {
    console.error('Error fetching story:', error);
    throw error;
  }
  return data;
};

export const getStoryEntries = async (storyId) => {
  const { data, error } = await supabase
    .from('story_entries')
    .select('*, profiles(username)')
    .eq('story_id', storyId)
    .order('order_index', { ascending: true });

  if (error) {
    console.error('Error fetching story entries:', error);
    throw error;
  }
  return data;
};


export const acceptCollaborationWithRole = async (storyId, userId, role, aiPaused = false) => {
  const { data, error } = await supabase
    .from('collaborators')
    .update({ status: 'Accepted', role: role, ai_paused: aiPaused })
    .eq('story_id', storyId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error accepting collaboration with role:', error);
    throw error;
  }
  return data;
};

export const deleteCollaborator = async (collabId) => {
  const { data, error } = await supabase
    .from('collaborators')
    .delete()
    .eq('id', collabId);

  if (error) {
    console.error('Error deleting collaborator:', error);
    throw error;
  }
  return data;
};

export const acceptCollaboration = async (storyId, userId) => {
  const { data, error } = await supabase
    .from('collaborators')
    .update({ status: 'Accepted' })
    .eq('story_id', storyId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error accepting collaboration:', error);
    throw error;
  }
  return data;
};

export const addCollaborator = async (storyId, username, role, characterId = null, aiPaused = false) => {
  // Look up the user UUID by username
  const { data: profileData, error: profileError } = await supabase
    .from('profiles')
    .select('id')
    .eq('username', username)
    .single();

  if (profileError || !profileData) {
      console.error('User not found by username:', profileError);
      throw new Error('User not found.');
  }

  const userId = profileData.id;

  const { data, error } = await supabase
    .from('collaborators')
    .insert([
      {
        story_id: storyId,
        user_id: userId,
        role: role,
        status: 'Invited',
        character_id: characterId,
        ai_paused: aiPaused
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error adding collaborator:', error);
    throw error;
  }
  return data;
};

export const deleteStory = async (storyId) => {
  const { data, error } = await supabase
    .from('stories')
    .delete()
    .eq('id', storyId);

  if (error) {
    console.error('Error deleting story:', error);
    throw error;
  }
  return data;
};

export const getCharacters = async (userId) => {
  const { data, error } = await supabase
    .from('characters')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching characters:', error);
    throw error;
  }
  return data;
};

export const createCharacter = async (userId, characterData) => {
  const { data, error } = await supabase
    .from('characters')
    .insert([{ user_id: userId, ...characterData }])
    .select()
    .single();

  if (error) {
    console.error('Error creating character:', error);
    throw error;
  }
  return data;
};

export const updateCharacterDB = async (characterId, characterData) => {
  const { data, error } = await supabase
    .from('characters')
    .update(characterData)
    .eq('id', characterId)
    .select()
    .single();

  if (error) {
    console.error('Error updating character:', error);
    throw error;
  }
  return data;
};

export const deleteCharacter = async (characterId) => {
  const { data, error } = await supabase
    .from('characters')
    .delete()
    .eq('id', characterId);

  if (error) {
    console.error('Error deleting character:', error);
    throw error;
  }
  return data;
};
export const getAllCharacters = async () => {
  const { data, error } = await supabase
    .from("characters")
    .select("*, profiles(username)")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data;
};

export const getCharacterStoriesInfo = async () => {
  const { data, error } = await supabase.rpc('get_character_story_stats');
  if (error) {
    console.error('Error fetching character stats:', error);
    return [];
  }
  return data;
};

export const updateCollaboratorCharacter = async (storyId, userId, characterId) => {
  const { data, error } = await supabase
    .from('collaborators')
    .update({ character_id: characterId })
    .eq('story_id', storyId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error updating collaborator character:', error);
    throw error;
  }
  return data;
};

export const updateCollaboratorAiPaused = async (storyId, userId, isPaused) => {
  const { data, error } = await supabase
    .from('collaborators')
    .update({ ai_paused: isPaused })
    .eq('story_id', storyId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error updating collaborator ai paused:', error);
    throw error;
  }
  return data;
};

export const getUserBookmarks = async (userId) => {
  const { data, error } = await supabase
    .from('bookmarks')
    .select('story_id, stories(*, chapters(id), profiles:owner_id(username))')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching user bookmarks:', error);
    throw error;
  }

  // The outer bookmarks array might be populated, but the RLS on 'stories' might return null for b.stories
  // Let's also attach the story_id so we can clean up broken bookmarks later if needed, but for now just filter.
  return data.map(b => b.stories).filter(s => s !== null);
};

export const toggleBookmark = async (userId, storyId) => {
  const { data: existing, error: checkError } = await supabase
    .from('bookmarks')
    .select('*')
    .eq('user_id', userId)
    .eq('story_id', storyId)
    .maybeSingle();

  if (checkError) {
    console.error("Error checking bookmark status", checkError);
    throw checkError;
  }

  if (existing) {
    // Remove bookmark
    const { error: delError } = await supabase
      .from('bookmarks')
      .delete()
      .eq('user_id', userId)
      .eq('story_id', storyId);
    if (delError) throw delError;
    return false; // Not bookmarked
  } else {
    // Add bookmark
    const { error: insError } = await supabase
      .from('bookmarks')
      .insert({ user_id: userId, story_id: storyId });
    if (insError) throw insError;
    return true; // Bookmarked
  }
};
